import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, MessageCircle, User, LogOut } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/blog", label: "Blog" },
    { path: "/resources", label: "Resources" },
    { path: "/data", label: "Research" },
    { path: "/share-story", label: "Stories" },
    { path: "/chat", label: "AI Companion" },
    { path: "https://ai-game-changer-2.preview.emergentagent.com/", label: "Tutorial", external: true },
    { path: "https://ai-game-changer-2.preview.emergentagent.com/empath-bots", label: "Empath Bots", external: true },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E5E7EB]/50" data-testid="navigation">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="font-heading text-lg font-bold text-[#1A2A3A]" data-testid="nav-logo">
            ThryvLife
          </Link>

          <div className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              link.external ? (
                <a key={link.path} href={link.path} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#1A2A3A]/70 hover:text-[#1A2A3A] transition-colors" data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {link.label}
                </a>
              ) : (
                <Link key={link.path} to={link.path} className={`text-xs font-medium transition-colors ${isActive(link.path) ? "text-[#4FB3D4]" : "text-[#1A2A3A]/70 hover:text-[#1A2A3A]"}`} data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {link.label}
                </Link>
              )
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-2">
            {user && user.name ? (
              <>
                <span className="text-xs text-[#1A2A3A]/60 mr-1" data-testid="nav-user-name">{user.name}</span>
                <Button variant="outline" size="sm" onClick={logout} className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB]" data-testid="nav-logout-btn">
                  <LogOut className="h-3 w-3 mr-1" /> Sign Out
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm" className="rounded-full px-3 py-1.5 text-xs border border-[#E5E7EB]" data-testid="nav-login-btn">
                  <User className="h-3 w-3 mr-1" /> Sign In
                </Button>
              </Link>
            )}
            <Link to="/chat">
              <Button variant="outline" size="sm" className="rounded-full px-3 py-1.5 text-xs border-[#4FB3D4]/20 hover:bg-[#4FB3D4]/5" data-testid="nav-chat-btn">
                <MessageCircle className="mr-1 h-3 w-3" /> Hope
              </Button>
            </Link>
            <Link to="/checkout">
              <Button size="sm" className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid="nav-cta-btn">
                Get Started
              </Button>
            </Link>
          </div>

          <button className="lg:hidden p-2 text-[#1A2A3A]" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden py-3 border-t border-[#E5E7EB]/50" data-testid="mobile-nav">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.external ? (
                  <a key={link.path} href={link.path} target="_blank" rel="noopener noreferrer" className="font-medium py-1.5 text-sm text-[#1A2A3A]/70" onClick={() => setIsOpen(false)}>
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.path} to={link.path} className={`font-medium py-1.5 text-sm ${isActive(link.path) ? "text-[#4FB3D4]" : "text-[#1A2A3A]/70"}`} onClick={() => setIsOpen(false)}>
                    {link.label}
                  </Link>
                )
              ))}
              <div className="flex gap-2 mt-2">
                {user && user.name ? (
                  <Button variant="outline" onClick={() => { logout(); setIsOpen(false); }} className="flex-1 rounded-full text-xs">
                    <LogOut className="mr-1 h-3 w-3" /> Sign Out
                  </Button>
                ) : (
                  <Link to="/login" onClick={() => setIsOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full rounded-full text-xs"><User className="mr-1 h-3 w-3" /> Sign In</Button>
                  </Link>
                )}
                <Link to="/checkout" onClick={() => setIsOpen(false)} className="flex-1">
                  <Button className="w-full bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full text-xs">Get Started</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
